import type {ReactElement} from 'react';
import React, { useEffect, useRef, useState} from 'react';
import {PANEL_TYPES} from '@Panels/masking-panel/masking-modal.types';
import type {
  PanelFooterPrimaryDefaultActionProps,
  PanelFooterSecondaryDefaultActionProps} from '@Components/panel';
import {
  Panel,
  PanelFooter,
  PanelFooterPrimaryDefaultAction,
  PanelFooterSecondaryDefaultAction,
  PanelHeader,
} from '@Components/panel';
import {useIsMobileWidth} from '@Hooks/useIsMobileWidth';
import FreehandMaskingPanel from '@Panels/masking-panel/components/freehand-masking-panel';
import ShapeMaskingPanel from '@Panels/masking-panel/components/shape-masking-panel';
import TextMaskingPanel from '@Panels/masking-panel/components/text-masking-panel';
import type {MaskingItemObject} from '@PosterWhiteboard/classes/item-masking.class';
import type { MaskingObject} from '@PosterWhiteboard/classes/masking/masking.class';
import {MaskingEffect, MaskingType} from '@PosterWhiteboard/classes/masking/masking.class';
import {getUniqueString} from '@Utils/string.util';
import type {CropData} from '@PosterWhiteboard/classes/item-crop.class';
import {setShowRemoveMask} from '@Panels/masking-panel/masking-panel-reducer';
import {Type} from '@Components/button';
import type {MaskingShapeObject} from '@PosterWhiteboard/classes/masking/masking-shape.class';
import {getSelectedGridItems} from '@Components/base-grid';
import type {MaskingFreehandObject} from '@PosterWhiteboard/classes/masking/masking-freehand.class';
import type {MaskingTextObject} from '@PosterWhiteboard/classes/masking/masking-text.class';
import {invalidateLineHeight} from '@PosterWhiteboard/libraries/text.library';
import {getRegularFont} from '@Libraries/font-library';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION, DROPDOWN_SIZE} from '@Components/dropdown-v2/dropdown.types';
import {DropdownDefaultSelectBox} from '@Components/dropdown-v2/components/dropdown-default-select-box';
import {v4 as uuidv4} from 'uuid';
import {ControlledList} from '@Components/controlled-list';
import type { ControlledListItem3} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import useCustomPanelClose from '@Panels/hooks/useCustomPanelClose';
import {closePanel} from '@Components/panel/panel-reducer';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './masking-panel.module.scss';

export interface MaskingPanelProps {
  imageUrl: string;
  initialMaskingData?: MaskingItemObject;
  panelId?: string;
  onMaskCTA: (maskCTAParam: Array<MaskingObject>) => void;
  cropData?: CropData;
  removeBackgroundCallback?: (isBackgroundRemoved: boolean, replaceImageCallback: (url: string) => void) => Promise<boolean>;
  isBackgroundRemoved?: boolean;
}

const panelTitle = 'Mask';

function MaskingPanel({...props}: MaskingPanelProps): ReactElement {
  const dispatch = useAppDispatch();
  const isMobileScreen = useIsMobileWidth();
  const panelId = useRef(props.panelId ?? getUniqueString());
  const showRemoveMask = useAppSelector((state) => {
    return state.masking.showRemoveMask;
  });
  const shapeMaskingGridID = useAppSelector((state) => {
    return state.shapeMasking.gridID;
  });
  const selectedGridItems: Array<any> | undefined = useAppSelector((state) => {
    if (shapeMaskingGridID) {
      return getSelectedGridItems(state, shapeMaskingGridID);
    }
    return undefined;
  });

  const onClose = async (): Promise<void> => {
    switch (panel) {
      case PANEL_TYPES.FREEHAND:
        await window.pointMask?.dispose();
        break;
      case PANEL_TYPES.SHAPE:
        await window.shapeMask?.dispose();
        break;
      case PANEL_TYPES.TEXT:
        await window.textMask?.dispose();
        break;
      default:
        throw new Error('Unhandled panel type in onClose()');
    }
    dispatch(closePanel(panelId.current));
  };
  useCustomPanelClose(panelId.current, onClose);

  useEffect(() => {
    dispatch(setShowRemoveMask(!!props.initialMaskingData));
  }, []);

  const getInitialPanel = (): PANEL_TYPES => {
    const DEFAULT_PANEL = PANEL_TYPES.FREEHAND;
    if (props.initialMaskingData) {
      switch (props.initialMaskingData.type) {
        case MaskingType.SHAPE:
          return PANEL_TYPES.SHAPE;

        case MaskingType.TEXT:
          return PANEL_TYPES.TEXT;

        default:
          return DEFAULT_PANEL;
      }
    }

    return DEFAULT_PANEL;
  };

  const [panel, setPanel] = useState<PANEL_TYPES>(getInitialPanel());

  const getHeader = (): ReactElement => {
    return <PanelHeader title={`${panelTitle}`} titleClasses="spacing-m-l-2" centerAlignTitle={isMobileScreen} onClose={onClose} showCloseButton />;
  };

  const getPaddingClasses = (): string => {
    return isMobileScreen ? 'spacing-p-t-1 spacing-p-r-3 spacing-p-l-3 spacing-p-b-4' : 'spacing-p-t-7 spacing-p-r-12 spacing-p-l-12 spacing-p-b-7';
  };

  const getContent = (): ReactElement => {
    return (
      <div className={`_full-width _full-height ${getPaddingClasses()}`}>
        {panel === PANEL_TYPES.FREEHAND ? (
          <FreehandMaskingPanel
            imageUrl={props.imageUrl}
            initialMaskingData={props.initialMaskingData?.type === MaskingType.FREEHAND ? (props.initialMaskingData as MaskingFreehandObject) : undefined}
            cropData={props.cropData}
            removeBackgroundCallback={props.removeBackgroundCallback}
            isBackgroundRemoved={props.isBackgroundRemoved}
          />
        ) : null}
        {panel === PANEL_TYPES.SHAPE ? (
          <ShapeMaskingPanel
            imageUrl={props.imageUrl}
            initialMaskingData={props.initialMaskingData?.type === MaskingType.SHAPE ? (props.initialMaskingData as MaskingShapeObject) : undefined}
            cropData={props.cropData}
          />
        ) : null}
        {panel === PANEL_TYPES.TEXT ? (
          <TextMaskingPanel
            imageUrl={props.imageUrl}
            initialMaskingData={props.initialMaskingData?.type === MaskingType.TEXT ? (props.initialMaskingData as MaskingTextObject) : undefined}
            cropData={props.cropData}
          />
        ) : null}
      </div>
    );
  };

  const getTextForMaskCTA = (): string | undefined => {
    if (isMobileScreen) {
      return undefined;
    }
    return showRemoveMask ? window.i18next.t('pmwjs_mask') : window.i18next.t('pmwjs_apply');
  };

  const getIconForMaskCTA = (): string | undefined => {
    return isMobileScreen ? 'icon-check' : undefined;
  };

  const getFooterPrimaryActions = (): Array<ReactElement> => {
    const primaryActions = [
      <PanelFooterPrimaryDefaultAction
        icon={getIconForMaskCTA()}
        key="btn-panel-mask"
        text={getTextForMaskCTA()}
        onClick={onMask}
        isLoading={false}
        title={window.i18next.t('pmwjs_mask')}
      />,
    ];

    if (showRemoveMask) {
      const removeMaskBtn = (
        <PanelFooterPrimaryDefaultAction
          type={Type.DANGER}
          key="btn-panel-remove-mask"
          text={isMobileScreen ? undefined : window.i18next.t('pmwjs_remove_mask')}
          onClick={onRemoveMask}
          isLoading={false}
          title={window.i18next.t('pmwjs_remove_mask')}
          icon={isMobileScreen ? 'icon-delete' : undefined}
        />
      );

      return [removeMaskBtn, ...primaryActions];
    }

    return primaryActions;
  };

  const onRemoveMask = (): void => {
    switch (panel) {
      case PANEL_TYPES.FREEHAND:
        window.pointMask?.removeAllPoints();
        break;
      case PANEL_TYPES.SHAPE:
        window.shapeMask?.removeMasking();
        break;
      case PANEL_TYPES.TEXT:
        window.textMask?.removeMasking();
        break;
      default:
        throw new Error('Unhandled panel type');
    }
  };

  const onMask = (): void => {
    const maskingItems: Array<MaskingObject> = [];
    switch (panel) {
      case PANEL_TYPES.FREEHAND:
        maskingItems.push(...getMaskingItemForFreehandMask());
        break;
      case PANEL_TYPES.SHAPE:
        maskingItems.push(...getMaskingItemForShapeMask());
        break;
      case PANEL_TYPES.TEXT:
        maskingItems.push(...getMaskingItemForTextMask());
        break;
      default:
        throw new Error('Unhandled panel type');
    }

    props.onMaskCTA(maskingItems);
  };

  const getMaskingItemForFreehandMask = (): Array<MaskingFreehandObject> => {
    if (showRemoveMask) {
      if (window.pointMask) {
        let points = window.pointMask.getPoints();
        const scale = window.pointMask.getScale();

        points = points.map((point) => {
          return {
            x: point.x / scale,
            y: point.y / scale,
          };
        });

        return [
          {
            maskPoints: points,
            isMaskCurved: window.pointMask.getMaskType(),
            maskEffect: window.pointMask.getMaskEffect(),
            imageWidth: window.pointMask.imageWidth,
            imageHeight: window.pointMask.imageHeight,
            type: MaskingType.FREEHAND,
          } as MaskingFreehandObject,
        ];
      }
      throw new Error('Bad code reach. One state tells that some making is applied, but the other says that there is no point mask canvas initialized in window');
    }
    return [];
  };

  const getMaskingItemForShapeMask = (): Array<MaskingShapeObject> => {
    if (showRemoveMask) {
      if (shapeMaskingGridID && selectedGridItems && window.shapeMask) {
        return [
          {
            angle: window.shapeMask.maskingShape?.angle,
            width: window.shapeMask.maskingShape?.width,
            height: window.shapeMask.maskingShape?.height,
            left: window.shapeMask.maskingShape?.left,
            top: window.shapeMask.maskingShape?.top,
            scaleX: window.shapeMask.maskingShape?.scaleX,
            scaleY: window.shapeMask.maskingShape?.scaleY,
            shapeName: selectedGridItems[0].additionalData.shapeName,
            insideMasking: window.shapeMask.insideMasking,
            maskEffect: window.shapeMask.maskEffect,
            imageWidth: window.shapeMask.imageWidth,
            imageHeight: window.shapeMask.imageHeight,
            type: MaskingType.SHAPE,
          } as MaskingShapeObject,
        ];
      }
      throw new Error('Bad code reach. One state tells that some making is applied, but the other says that there is no grid');
    }

    return [];
  };

  const getMaskingItemForTextMask = (): Array<MaskingTextObject> => {
    if (showRemoveMask) {
      if (window.textMask && window.textMask.maskingText && window.textMask.maskingTextStyle) {
        return [
          {
            angle: window.textMask.maskingText?.angle,
            width: Math.floor(window.textMask.maskingText?.width),
            height: Math.floor(window.textMask.maskingText?.height),
            scaleX: window.textMask?.maskingText.scaleX,
            scaleY: window.textMask?.maskingText.scaleY,
            left: Math.floor(window.textMask?.maskingText.left),
            top: Math.floor(window.textMask?.maskingText.top),
            text: window.textMask?.maskingText.text,
            imageWidth: window.textMask.imageWidth,
            imageHeight: window.textMask.imageHeight,
            type: MaskingType.TEXT,
            maskEffect: MaskingEffect.NONE,
            insideMasking: true,
            textStyles: {
              letterSpacing: window.textMask?.maskingText.charSpacing,
              fontSize: window.textMask?.maskingText.fontSize,
              leading: invalidateLineHeight(window.textMask?.maskingText.lineHeight),
              textAlign: window.textMask.getTextAlign(window.textMask?.maskingText.textAlign),
              fontFamily: getRegularFont(window.textMask?.maskingTextStyle.fontFamily),
              isBold: window.textMask?.maskingTextStyle.isBold,
              isItalic: window.textMask?.maskingTextStyle.isItalic,
              underLine: window.textMask?.maskingText.underline,
              lineThrough: window.textMask?.maskingText.linethrough,
            },
          },
        ];
      }
      throw new Error('Bad code reach. One state tells that some making is applied, but the other says that there is no text mask canvas initialized in window');
    }
    return [];
  };

  const getPanelSwitchingDropdown = (): Array<ReactElement> => {
    return [
      <Dropdown
        key="panel-select-dropdown"
        position={DROPDOWN_POSITION.FIXED}
        alignment={ALIGNMENT_TYPE.BOTTOM_END}
        className={`${styles.dropdown} flex-center`}
        popUpHasCustomWidth
        selector={<DropdownDefaultSelectBox id={`${uuidv4()}-dropdown`} text={`${panel}`} size={DROPDOWN_SIZE.SMALL} isLoading={false} />}
        popup={getDropdownList()}
        popupSpacingClass="spacing-p-b-0 spacing-p-r-2 spacing-p-l-2 spacing-p-t-2"
      />,
    ];
  };

  const getDropdownList = (): ReactElement => {
    const listItems = getDropdownListItems();
    return (
      <ControlledList
        items={listItems}
        className={styles.dropdownList}
        onClick={async (id: PANEL_TYPES): Promise<void> => {
          switch (id) {
            case PANEL_TYPES.FREEHAND:
              await onFreehandMaskSelected();
              break;
            case PANEL_TYPES.TEXT:
              await onTextMaskSelected();
              break;
            case PANEL_TYPES.SHAPE:
              await onShapeMaskSelected();
              break;
            default:
              throw new Error('Unhandled panel type');
          }
        }}
      />
    );
  };

  const getDropdownListItems = (): Array<ControlledListItem3> => {
    return [
      {
        id: `${PANEL_TYPES.FREEHAND}`,
        text: PANEL_TYPES.FREEHAND,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
        isSelected: panel === PANEL_TYPES.FREEHAND,
      },
      {
        id: `${PANEL_TYPES.SHAPE}`,
        text: PANEL_TYPES.SHAPE,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
        isSelected: panel === PANEL_TYPES.SHAPE,
      },
      {
        id: `${PANEL_TYPES.TEXT}`,
        text: PANEL_TYPES.TEXT,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
        isSelected: panel === PANEL_TYPES.TEXT,
      },
    ];
  };

  const onFreehandMaskSelected = async (): Promise<void> => {
    setPanel(PANEL_TYPES.FREEHAND);
  };

  const onShapeMaskSelected = async (): Promise<void> => {
    setPanel(PANEL_TYPES.SHAPE);
  };

  const onTextMaskSelected = async (): Promise<void> => {
    setPanel(PANEL_TYPES.TEXT);
  };

  const getPanelSwitchingToggles = (): Array<ReactElement> => {
    const freeHandBtnProps: PanelFooterSecondaryDefaultActionProps | PanelFooterPrimaryDefaultActionProps = {
      key: 'btn-freehand-panel',
      text: PANEL_TYPES.FREEHAND,
      onClick: onFreehandMaskSelected,
    };
    const shapeBtnProps: PanelFooterSecondaryDefaultActionProps | PanelFooterPrimaryDefaultActionProps = {
      key: 'btn-shape-panel',
      text: PANEL_TYPES.SHAPE,
      onClick: onShapeMaskSelected,
    };
    const textBtnProps: PanelFooterSecondaryDefaultActionProps | PanelFooterPrimaryDefaultActionProps = {
      key: 'btn-text-panel',
      text: PANEL_TYPES.TEXT,
      onClick: onTextMaskSelected,
    };

    return [
      panel !== PANEL_TYPES.FREEHAND ? (
        <PanelFooterSecondaryDefaultAction key={freeHandBtnProps.key} text={freeHandBtnProps.text} onClick={freeHandBtnProps.onClick} />
      ) : (
        <PanelFooterPrimaryDefaultAction key={freeHandBtnProps.key} text={freeHandBtnProps.text} onClick={freeHandBtnProps.onClick} />
      ),
      panel !== PANEL_TYPES.SHAPE ? (
        <PanelFooterSecondaryDefaultAction key={shapeBtnProps.key} text={shapeBtnProps.text} onClick={shapeBtnProps.onClick} />
      ) : (
        <PanelFooterPrimaryDefaultAction key={shapeBtnProps.key} text={shapeBtnProps.text} onClick={shapeBtnProps.onClick} />
      ),
      panel !== PANEL_TYPES.TEXT ? (
        <PanelFooterSecondaryDefaultAction key={textBtnProps.key} text={textBtnProps.text} onClick={textBtnProps.onClick} />
      ) : (
        <PanelFooterPrimaryDefaultAction key={textBtnProps.key} text={textBtnProps.text} onClick={textBtnProps.onClick} />
      ),
    ];
  };

  const getFooterSecondaryActions = (): Array<ReactElement> => {
    return isMobileScreen ? getPanelSwitchingDropdown() : getPanelSwitchingToggles();
  };

  const getFooter = (): ReactElement => {
    return (
      <PanelFooter
        className={`${isMobileScreen ? 'spacing-p-3' : 'spacing-p-t-5 spacing-p-r-6 spacing-p-b-5 spacing-p-l-6'}`}
        primaryActions={getFooterPrimaryActions()}
        secondaryActions={getFooterSecondaryActions()}
        border
      />
    );
  };

  return <Panel panelHeader={getHeader()} panelContent={getContent()} panelFooter={getFooter()} />;
}

export default MaskingPanel;
